import React, { useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Card  from "@react/shared/ui/Card"
import Input from "@react/shared/ui/Input"
import Label from "@react/shared/ui/Label"
import { useQuoteEditor } from "../context/QuoteEditorContext"
import { useDecimalDraft } from "./DecimalInput"
import { nodeTotals, isOption, directChildrenCount, collectVatBreakdown, collectByCostCategory } from "../helpers/treeMath"
import { formatNumber, roundNumber } from "../helpers/formatting"
import { roundMoney } from "@react/helpers/helpers"
import { DaysChip, daysBetween, addDays, toISO } from "./MetaCard"

const stripTags = html => {
  const doc = new DOMParser().parseFromString(html || "", "text/html")
  doc.body.querySelectorAll("br").forEach(br => br.replaceWith(" "))
  return (doc.body.textContent || "").replace(/ /g, " ").trim()
}

const FieldLabel = ({ required, children }) => (
  <Label size="sm" tone="default" className="flex items-center gap-1">
    {children}
    {required && <span className="text-rose-500">*</span>}
  </Label>
)

const StackedDateField = ({ label, value, onChange, suffix }) => (
  <div className="space-y-1 min-w-0">
    <div className="flex items-center gap-2">
      <FieldLabel>{label}</FieldLabel>
      {suffix}
    </div>
    <Input
      type     = "date"
      value    = {toISO(value)}
      onChange = {e => onChange(e.target.value)}
      className = "w-full"
    />
  </div>
)

const ResourceField = ({ label, name, href, icon }) => (
  <div className="space-y-1">
    <FieldLabel>{label}</FieldLabel>
    {name ? (
      <a
        href      = {href}
        target    = "_blank"
        rel       = "noopener noreferrer"
        className = "group flex items-center gap-2 w-full px-2.5 h-9 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 hover:border-brand-400 hover:bg-brand-50/40 hover:text-brand-700 transition-colors no-underline"
      >
        <FontAwesomeIcon icon={icon} className="text-slate-400 group-hover:text-brand-500 shrink-0" />
        <span className="truncate font-medium flex-1">{name}</span>
        <FontAwesomeIcon icon="arrow-up-right-from-square" className="text-[10px] text-slate-300 group-hover:text-brand-500 shrink-0" />
      </a>
    ) : (
      <div className="flex items-center px-2.5 h-9 rounded-md border border-dashed border-slate-200 text-[13px] text-slate-400">—</div>
    )}
  </div>
)

const PDF_OPTIONS = [
  { field: "showSiteAddress",       label: "quotes.new.show_site_address"       },
  { field: "showServiceComponents", label: "quotes.new.show_service_components" },
]

const PropertyFieldRow = ({ field, dispatch }) => {
  const update = (key, value) =>
    dispatch({ type: "UPDATE_PROPERTY_FIELD", payload: { uid: field._uid, field: key, value } })

  switch (field.field_type) {
    case "string":
      return (
        <textarea
          rows      = {2}
          value     = {field.string_answer || ""}
          onChange  = {e => update("string_answer", e.target.value)}
          className = "w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
        />
      )
    case "boolean":
      return (
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type     = "checkbox"
            checked  = {!!field.boolean_answer}
            onChange = {e => update("boolean_answer", e.target.checked)}
            className= "h-4 w-4 rounded border-slate-300"
          />
          {I18n.t("quotes.builder.settings.yes")}
        </label>
      )
    case "date":
      return (
        <Input
          type     = "date"
          value    = {field.date_answer || ""}
          onChange = {e => update("date_answer", e.target.value)}
        />
      )
    case "integer":
      return (
        <Input
          type     = "number"
          step     = "1"
          value    = {field.integer_answer ?? ""}
          onChange = {e => update("integer_answer", e.target.value === "" ? null : parseInt(e.target.value, 10))}
        />
      )
    case "float":
      return (
        <Input
          type     = "number"
          step     = "0.01"
          value    = {field.float_answer ?? ""}
          onChange = {e => update("float_answer", e.target.value === "" ? null : parseFloat(e.target.value))}
        />
      )
    case "options":
      return (
        <select
          className="w-full h-9 text-sm rounded-md border border-slate-300 px-2 bg-white"
          value={field.string_answer || ""}
          onChange={e => update("string_answer", e.target.value)}
        >
          <option value="">{I18n.t("quotes.builder.settings.select_option")}</option>
          {(field.options || []).map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      )
    case "link_url":
      return (
        <Input
          type     = "url"
          value    = {field.string_answer || ""}
          onChange = {e => update("string_answer", e.target.value)}
          placeholder = {I18n.t("quotes.builder.settings.link_placeholder")}
        />
      )
    case "sub_title":
      return null
    default:
      return (
        <div className="text-xs text-slate-400">{I18n.t("quotes.builder.settings.unsupported_type", { type: field.field_type })}</div>
      )
  }
}

const SummaryTab = () => {
  const { state, dispatch } = useQuoteEditor()
  const {
    number, title, client, site, creationDate, expirationDate,
    expirationDays, managers, managerId, propertyFields, sections, isTemplate,
    partners, partnerId, partnerFrozen, partnerLocked,
  } = state

  const symbol   = state.currencySymbol || "€"
  const discount = parseFloat(state.discount) || 0
  const discountInput = useDecimalDraft({
    value:    discount || "",
    onChange: raw => {
      const value = parseFloat(raw.replace(",", ".")) || 0
      dispatch({ type: "SET_DISCOUNT", payload: Math.min(100, Math.max(0, value)) })
    },
  })

  const setField = (field, value) => dispatch({ type: "SET_QUOTE_FIELD", payload: { field, value } })
  const setTitle = value => dispatch({ type: "UPDATE_TITLE", payload: value })

  const setCreationDate = iso => {
    if (!iso) return
    const currentDays = daysBetween(creationDate, expirationDate)
    const days = currentDays > 0 ? currentDays : (expirationDays || 30)
    const newExpiration = addDays(iso, days)
    dispatch({
      type: "UPDATE_QUOTE_META",
      payload: { fields: { creationDate: iso, expirationDate: newExpiration || expirationDate, expirationDays: days } },
    })
  }
  const setExpirationDate = iso => {
    if (!iso) return
    const days = daysBetween(creationDate, iso)
    dispatch({
      type: "UPDATE_QUOTE_META",
      payload: { fields: { expirationDate: iso, expirationDays: days >= 0 ? days : expirationDays } },
    })
  }
  const setValidityDays = days => {
    const newExpiration = addDays(creationDate, days)
    dispatch({
      type: "UPDATE_QUOTE_META",
      payload: { fields: { expirationDays: days, expirationDate: newExpiration || expirationDate } },
    })
  }
  const daysLive = daysBetween(creationDate, expirationDate) || expirationDays

  const activeSections = (sections || []).filter(s => !s._destroy)

  const { subtotalHT, subtotalCost, totalsByLot, vatBreakdown, labourHours } = useMemo(() => {
    const totalsByLot = activeSections.map((section, idx) => {
      const t = nodeTotals(section)
      return {
        uid:    section._uid,
        option: isOption(section),
        index:  idx + 1,
        name:   stripTags(section.description),
        items:  directChildrenCount(section),
        cost:   roundNumber(t.cost),
        sale:   roundNumber(t.total),
        margin: roundNumber(t.total - t.cost),
        marginPct: t.total > 0 ? ((t.total - t.cost) / t.total) * 100 : 0,
      }
    })
    const counted      = totalsByLot.filter(l => !l.option)
    const subtotalHT   = roundNumber(counted.reduce((s, l) => s + l.sale, 0))
    const subtotalCost = roundNumber(counted.reduce((s, l) => s + l.cost, 0))
    const vats = {}
    activeSections.forEach(s => collectVatBreakdown(s, vats))
    const labourHours = roundNumber(activeSections.reduce((sum, section) => {
      const buckets = collectByCostCategory(section)
      return sum + Object.values(buckets).reduce((inner, m) => inner + (m.isLabour ? m.hours : 0), 0)
    }, 0))
    return { subtotalHT, subtotalCost, totalsByLot, vatBreakdown: vats, labourHours }
  }, [activeSections])

  const discountAmount = roundNumber(subtotalHT * discount / 100)
  const totalHT        = roundNumber(subtotalHT - discountAmount)

  let totalVAT = 0
  Object.entries(vatBreakdown).forEach(([pct, base]) => {
    const discounted = base * (1 - discount / 100)
    totalVAT += roundMoney(parseFloat(pct) * discounted / 100)
  })
  totalVAT = roundMoney(totalVAT)
  const totalTTC = roundNumber(totalHT + totalVAT)

  const marginAmount = roundNumber(subtotalHT - subtotalCost)
  const marginPct    = subtotalHT > 0 ? roundNumber(marginAmount / subtotalHT * 100) : 0

  const ringPct  = Math.max(0, Math.min(100, marginPct))
  const ringStyle = {
    background: `conic-gradient(#16A34A 0% ${ringPct}%, #F1F5F9 ${ringPct}% 100%)`,
  }

  const ROW = "flex justify-between items-baseline"
  const SEP = "h-px bg-slate-200 my-1"
  const k   = "text-slate-500"
  const v   = "tabular-nums text-slate-700 font-medium"

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* ─── Informations générales ───────────────────────────── */}
      <Card padding="lg" className="space-y-4">
        <div className="text-sm font-semibold text-slate-900">
          {I18n.t("quotes.builder.summary_tab.general_info_title")}
        </div>

        {!isTemplate && number && (
          <div className="space-y-1">
            <FieldLabel>{I18n.t("quotes.builder.summary_tab.reference_label")}</FieldLabel>
            <div className="inline-flex items-center px-2.5 h-9 rounded-md bg-slate-50 font-mono text-[13px] font-semibold text-slate-700">{number}</div>
          </div>
        )}

        {!isTemplate && (
          <>
            <ResourceField
              label = {I18n.t("quotes.builder.summary_tab.client_label")}
              name  = {client?.name}
              href  = {client?.id ? `/clients/${client.id}` : null}
              icon  = "user-circle"
            />
            <ResourceField
              label = {I18n.t("quotes.builder.summary_tab.site_label")}
              name  = {site?.name}
              href  = {site?.id ? `/sites/${site.id}` : null}
              icon  = "building"
            />
          </>
        )}

        <div className="space-y-1">
          <FieldLabel required={isTemplate}>
            {isTemplate
              ? I18n.t("quotes.project_templates.name_label")
              : I18n.t("quotes.builder.summary_tab.title_label")}
          </FieldLabel>
          <Input
            type        = "text"
            value       = {title || ""}
            placeholder = {isTemplate
              ? I18n.t("quotes.project_templates.name_placeholder")
              : I18n.t("quotes.new.title_placeholder")}
            onChange    = {e => setTitle(e.target.value)}
          />
        </div>

        {!isTemplate && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <StackedDateField
              label    = {I18n.t("quotes.builder.meta.issued_on")}
              value    = {creationDate}
              onChange = {setCreationDate}
            />
            <StackedDateField
              label    = {I18n.t("quotes.builder.meta.expires_on")}
              value    = {expirationDate}
              onChange = {setExpirationDate}
              suffix   = {<DaysChip days={daysLive} onCommit={setValidityDays} />}
            />
          </div>
        )}

        {!isTemplate && (
          <div className="space-y-1">
            <FieldLabel required>{I18n.t("quotes.builder.settings.manager_label")}</FieldLabel>
            <select
              className={`w-full h-9 text-sm rounded-md border px-2 bg-white outline-none focus:ring-[3px] focus:ring-brand-500/20 ${managerId ? "border-slate-300 hover:border-slate-400 focus:border-brand-500" : "border-rose-400 focus:border-rose-500"}`}
              value={managerId || ""}
              onChange={e => setField("managerId", e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              <option value="">{I18n.t("quotes.builder.settings.select_option")}</option>
              {(managers || []).map(m => (
                <option key={m.id} value={m.id}>{m.public_name || m.name || m.email}</option>
              ))}
            </select>
          </div>
        )}

        {!isTemplate && (partners || []).length > 0 && (
          <div className="space-y-1">
            <FieldLabel required>
              {I18n.t("partners.issuing_partner")}
              {partnerLocked && (
                <span className="ml-1 text-slate-400" title={I18n.t(partnerFrozen ? "partners.frozen_hint" : "partners.locked_hint")}>
                  <FontAwesomeIcon icon="info-circle" />
                </span>
              )}
            </FieldLabel>
            <select
              className={`w-full h-9 text-sm rounded-md border px-2 bg-white outline-none focus:ring-[3px] focus:ring-brand-500/20 disabled:bg-slate-50 disabled:text-slate-500 ${partnerId ? "border-slate-300 hover:border-slate-400 focus:border-brand-500" : "border-rose-400 focus:border-rose-500"}`}
              value={partnerId || ""}
              disabled={partnerLocked}
              onChange={e => setField("partnerId", e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              <option value="">{I18n.t("client.partners.select_partner")}</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.id}>{partner.name}</option>
              ))}
            </select>
          </div>
        )}

        {!isTemplate && (
          <div className="space-y-2 pt-3 border-t border-slate-200">
            <FieldLabel>{I18n.t("quotes.builder.summary_tab.pdf_options")}</FieldLabel>
            {PDF_OPTIONS.map(({ field, label }) => (
              <label key={field} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type      = "checkbox"
                  checked   = {!!state[field]}
                  onChange  = {e => setField(field, e.target.checked)}
                  className = "h-4 w-4 rounded border-slate-300"
                />
                {I18n.t(label)}
              </label>
            ))}
          </div>
        )}

        {propertyFields && propertyFields.length > 0 && (
          <div className="space-y-3">
            {propertyFields.map(field => (
              field.field_type === "sub_title" ? (
                <div key={field._uid} className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold pt-2 border-t border-slate-200">
                  {field.title}
                </div>
              ) : (
                <div key={field._uid} className="space-y-1">
                  <FieldLabel required={field.required}>{field.title}</FieldLabel>
                  <PropertyFieldRow field={field} dispatch={dispatch} />
                </div>
              )
            ))}
          </div>
        )}
      </Card>

      {/* ─── Synthèse financière ──────────────────────────────── */}
      <Card padding="lg" className="space-y-4">
        <div className="text-sm font-semibold text-slate-900">
          {I18n.t("quotes.builder.summary_tab.financial_summary_title")}
        </div>

        <div className="flex flex-col gap-[7px] text-[13px] tabular-nums">
          <div className={ROW}>
            <span className={k}>{I18n.t("quotes.builder.summary_tab.total_cost")}</span>
            <span className={v}>{formatNumber(subtotalCost)} {symbol}</span>
          </div>
          <div className={ROW}>
            <span className={k}>{I18n.t("quotes.builder.summary_tab.total_sale_brut")}</span>
            <span className={v}>{formatNumber(subtotalHT)} {symbol}</span>
          </div>
          <div className={`${ROW} items-center`}>
            <label htmlFor="qb-global-discount" className={k}>{I18n.t("quotes.builder.summary_tab.global_discount")}</label>
            <span className="inline-flex items-center gap-1">
              <input
                id        = "qb-global-discount"
                {...discountInput}
                placeholder = "0"
                disabled  = {state.readOnly}
                className = "w-16 h-7 px-2 text-right text-[13px] tabular-nums border border-slate-200 rounded-md outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20 disabled:bg-slate-50"
              />
              <span className="text-slate-500">%</span>
            </span>
          </div>
          {discountAmount > 0 && (
            <div className={ROW}>
              <span className={k}>{I18n.t("quotes.builder.summary.discount", { discount })}</span>
              <span className="tabular-nums text-emerald-600 font-medium">− {formatNumber(discountAmount)} {symbol}</span>
            </div>
          )}
          <div className={SEP} />
          <div className={ROW}>
            <span className={k}>{I18n.t("quotes.builder.summary_tab.total_ht_net")}</span>
            <span className={v}>{formatNumber(totalHT)} {symbol}</span>
          </div>
          <div className={ROW}>
            <span className={k}>{I18n.t("quotes.builder.summary.vat")}</span>
            <span className={v}>{formatNumber(totalVAT)} {symbol}</span>
          </div>
          <div className={SEP} />
          <div className={`${ROW} pt-1.5 pb-0.5`}>
            <span className="text-[13px] text-slate-900 font-semibold">{I18n.t("quotes.builder.summary.total_incl_tax")}</span>
            <span className="tabular-nums text-slate-900 font-bold text-[20px] -tracking-[0.015em]">{formatNumber(totalTTC)} {symbol}</span>
          </div>
          {labourHours > 0 && (
            <div className={`${ROW} mt-1 rounded-md bg-slate-100 px-2.5 py-2`}>
              <span className="text-[13px] text-slate-700 font-semibold inline-flex items-center gap-1.5">
                <FontAwesomeIcon icon="helmet-safety" className="text-slate-400" />
                {I18n.t("quotes.builder.summary_tab.labor_hours")}
              </span>
              <span className="tabular-nums text-slate-900 font-bold text-[17px] -tracking-[0.015em]">{formatNumber(labourHours)} h</span>
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-slate-200">
          <Label size="md" className="text-slate-500 mb-3 block">{I18n.t("quotes.builder.summary_tab.global_margin")}</Label>
          <div className="flex items-center gap-4">
            <div
              className="relative flex items-center justify-center flex-none rounded-full before:content-[''] before:absolute before:inset-[9px] before:bg-white before:rounded-full"
              style={{ width: 88, height: 88, ...ringStyle }}
            >
              <span className="relative text-[17px] font-bold -tracking-[0.015em] text-slate-900">
                {Math.round(marginPct)}<span className="text-[11px] text-slate-400 font-medium">%</span>
              </span>
            </div>
            <div className="flex-1 text-[12.5px] text-slate-500 leading-[1.5]">
              <strong className="block mb-0.5 text-slate-900 font-semibold text-[14px]">
                {I18n.t("quotes.builder.summary.margin_amount", { amount: `${formatNumber(marginAmount)} ${symbol}` })}
              </strong>
            </div>
          </div>
        </div>
      </Card>

      </div>

      {/* ─── Décomposition par lot ─────────────────────────────── */}
      <Card padding="lg" className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-sm font-semibold text-slate-900">
            {I18n.t("quotes.builder.summary_tab.breakdown_by_lot_title")}
          </div>
          {totalsByLot.length > 0 && (
            <div className="text-[11px] text-slate-400 tabular-nums">
              {I18n.t("quotes.builder.summary_tab.lot_count", { count: totalsByLot.length })}
            </div>
          )}
        </div>

        {totalsByLot.length === 0 ? (
          <div className="text-xs text-slate-400">{I18n.t("quotes.builder.summary_tab.no_lots")}</div>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="py-2 px-1 text-left w-[40px]">{I18n.t("quotes.builder.summary_tab.lot_number")}</th>
                  <th className="py-2 px-1 text-left">{I18n.t("quotes.builder.summary_tab.lot_name")}</th>
                  <th className="py-2 px-2 text-right whitespace-nowrap min-w-[130px] w-[130px]">{I18n.t("quotes.builder.summary_tab.lot_cost")}</th>
                  <th className="py-2 px-2 text-right whitespace-nowrap min-w-[130px] w-[130px]">{I18n.t("quotes.builder.summary_tab.lot_sale")}</th>
                  <th className="py-2 px-2 text-right whitespace-nowrap min-w-[130px] w-[130px]">{I18n.t("quotes.builder.summary_tab.lot_margin")}</th>
                </tr>
              </thead>
              <tbody>
                {totalsByLot.map(lot => {
                  const isEmpty = lot.cost === 0 && lot.sale === 0
                  const tone =
                    isEmpty             ? "text-slate-400"   :
                    lot.marginPct >= 25 ? "text-emerald-600" :
                    lot.marginPct >= 15 ? "text-amber-600"   :
                                          "text-rose-600"
                  const displayName = lot.name || I18n.t("quotes.builder.summary_tab.lot_unnamed")
                  return (
                    <tr key={lot.uid} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
                      <td className="py-2 px-1 text-slate-400 tabular-nums align-top">{lot.index}</td>
                      <td className="py-2 px-1 align-top">
                        <div className={`font-medium ${lot.name ? "text-slate-900" : "text-slate-400 italic"}`}>
                          {displayName}{lot.option && <span title={I18n.t("qline_groups.form.is_option")}> *</span>}
                        </div>
                        {lot.items > 0 && (
                          <div className="text-[11px] text-slate-400 mt-0.5 tabular-nums">
                            {I18n.t("quotes.builder.summary_tab.lot_items", { count: lot.items })}
                          </div>
                        )}
                      </td>
                      <td className={`py-2 px-2 text-right tabular-nums whitespace-nowrap align-top ${isEmpty ? "text-slate-300" : "text-slate-600"}`}>
                        {isEmpty ? "—" : `${formatNumber(lot.cost)} ${symbol}`}
                      </td>
                      <td className={`py-2 px-2 text-right tabular-nums whitespace-nowrap align-top ${isEmpty ? "text-slate-300" : "text-slate-900 font-medium"}`}>
                        {isEmpty ? "—" : `${formatNumber(lot.sale)} ${symbol}`}
                      </td>
                      <td className={`py-2 px-2 text-right tabular-nums whitespace-nowrap align-top font-semibold ${tone}`}>
                        {lot.sale > 0 ? (
                          <>
                            <div>{formatNumber(lot.margin)} {symbol}</div>
                            <div className="text-[11px] font-medium opacity-80 mt-0.5">{Math.round(lot.marginPct)} %</div>
                          </>
                        ) : "—"}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50/60">
                  <td className="py-2.5 px-1" />
                  <td className="py-2.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-slate-700">
                    {I18n.t("quotes.builder.summary_tab.lot_total")}
                  </td>
                  <td className="py-2.5 px-2 text-right tabular-nums whitespace-nowrap text-slate-700 font-medium">
                    {formatNumber(subtotalCost)} {symbol}
                  </td>
                  <td className="py-2.5 px-2 text-right tabular-nums whitespace-nowrap text-slate-900 font-semibold">
                    {formatNumber(subtotalHT)} {symbol}
                  </td>
                  <td className="py-2.5 px-2 text-right tabular-nums whitespace-nowrap font-bold text-slate-900">
                    {subtotalHT > 0 ? (
                      <>
                        <div>{formatNumber(marginAmount)} {symbol}</div>
                        <div className="text-[11px] font-medium opacity-80 mt-0.5">{Math.round(marginPct)} %</div>
                      </>
                    ) : "—"}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

export default SummaryTab
